import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import StandardSeal from "@/components/StandardSeal";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY",
  "LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND",
  "OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","National",
];

const ORG_TYPES = [
  { v: "campaign", l: "Campaign" },
  { v: "pac", l: "PAC" },
  { v: "org", l: "Organization / non-profit" },
  { v: "consultant", l: "Consultant" },
];

export default function AlignmentApplyPage() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const tierInterest = new URLSearchParams(loc.search).get("tier") || "";

  const [declaration, setDeclaration] = useState("");
  const [counselFooter, setCounselFooter] = useState("");

  const [form, setForm] = useState({
    org_name: "",
    org_type: "campaign",
    principal_name: user?.name || "",
    principal_role: "",
    states: "",
    office_or_mission: "",
    mission_statement: "",
    funding_attestation: false,
    standard_signature: "",
    standard_agreed: false,
    tier_interest: tierInterest,
    contact_email: user?.email || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [submittedId, setSubmittedId] = useState(null);

  useEffect(() => {
    api.get("/alignment/criteria").then((r) => {
      setDeclaration(r.data.declaration || "");
      setCounselFooter(r.data.counsel_footer || "");
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.funding_attestation) {
      setErr("The funding attestation must be affirmed to submit.");
      return;
    }
    if (!form.standard_agreed || !form.standard_signature.trim()) {
      setErr("Type your full name and check the Standard signature box.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/alignment/apply", form);
      setSubmittedId(data.id);
      if (data.status === "approved") {
        // Already approved — bounce to the portal.
        setTimeout(() => nav(user?.role ? "/client" : "/login"), 1500);
      }
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (submittedId) {
    return (
      <>
        <PublicNav />
        <Section testId="alignment-submitted">
          <div className="dt6-card p-10 flex flex-col items-center text-center dt6-fade-up" style={{ borderColor: "var(--dt6-primary)" }}>
            <div className="dt6-scale-in"><StandardSeal size={120} /></div>
            <h1 className="font-heading text-3xl font-bold mt-6 mb-3">Application received.</h1>
            <p className="text-sm text-[color:var(--dt6-muted)] max-w-lg mb-6">
              A DT6 principal will review your application. If approved, you'll receive the
              "You are armed" email with checkout access. If declined, you'll receive a
              mission-based one-line response.
            </p>
            <div className="dt6-chip">/ APPLICATION ID · {submittedId}</div>
          </div>
        </Section>
        <PublicFooter />
      </>
    );
  }

  return (
    <>
      <PublicNav />
      <div className="dt6-flag-wave">
        <Section eyebrow="/ THE OATH · SIGN TO BE ARMED" testId="alignment-apply">
          {/* Centered "document" surface — deliberately narrow to feel like a contract sheet */}
          <div className="max-w-3xl mx-auto">
            {/* Document masthead */}
            <div className="flex flex-col items-center text-center mb-8 dt6-fade-up">
              <div className="dt6-scale-in"><StandardSeal size={140} /></div>
              <h1 className="font-heading text-4xl md:text-5xl font-black mt-6 leading-tight">
                The DT6 Standard<br />
                <span style={{ color: "var(--dt6-primary)" }}>Application &amp; Oath</span>
              </h1>
              <div className="dt6-stripe-underline mt-4" style={{ maxWidth: 240 }} />
            </div>

            {/* Verbatim declaration + counsel footer */}
            <blockquote
              className="dt6-card p-6 md:p-8 border-l-4 text-base leading-relaxed mb-8 dt6-fade-up"
              style={{ borderLeftColor: "var(--dt6-primary)", animationDelay: "160ms" }}
              data-testid="alignment-declaration"
            >
              {declaration || "…"}
            </blockquote>

            {/* The signable document */}
            <form
              onSubmit={submit}
              className="dt6-card p-6 md:p-10 dt6-fade-up"
              style={{ background: "#1A1F2A", animationDelay: "260ms" }}
              data-testid="alignment-form"
            >
              {err && (
                <div className="text-sm text-[color:var(--dt6-danger)] mb-4" data-testid="alignment-err">
                  <AlertCircle size={14} className="inline-block mr-1" /> {err}
                </div>
              )}

              {/* Article I — the organization */}
              <div className="mb-8">
                <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--dt6-primary)] mb-4">
                  / ARTICLE I · THE ORGANIZATION
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="dt6-label">Organization name</label>
                    <input required className="dt6-input" data-testid="al-org-name"
                           value={form.org_name} onChange={(e) => set("org_name", e.target.value)} />
                  </div>
                  <div>
                    <label className="dt6-label">Organization type</label>
                    <select className="dt6-select" data-testid="al-org-type"
                            value={form.org_type} onChange={(e) => set("org_type", e.target.value)}>
                      {ORG_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="dt6-label">State(s) of operation</label>
                    <input required className="dt6-input" data-testid="al-states"
                           value={form.states} onChange={(e) => set("states", e.target.value)}
                           placeholder="PA, OH, or National" list="al-state-list" />
                    <datalist id="al-state-list">
                      {STATES.map((s) => <option key={s} value={s} />)}
                    </datalist>
                  </div>
                  <div>
                    <label className="dt6-label">Office or mission</label>
                    <input required className="dt6-input" data-testid="al-office"
                           value={form.office_or_mission} onChange={(e) => set("office_or_mission", e.target.value)}
                           placeholder="US House PA-08, Election integrity, etc." />
                  </div>
                </div>
              </div>

              {/* Article II — the principal */}
              <div className="mb-8">
                <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--dt6-primary)] mb-4">
                  / ARTICLE II · THE PRINCIPAL
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="dt6-label">Principal name</label>
                    <input required className="dt6-input" data-testid="al-principal-name"
                           value={form.principal_name} onChange={(e) => set("principal_name", e.target.value)} />
                  </div>
                  <div>
                    <label className="dt6-label">Principal role / title</label>
                    <input required className="dt6-input" data-testid="al-principal-role"
                           value={form.principal_role} onChange={(e) => set("principal_role", e.target.value)}
                           placeholder="Campaign Manager, PAC Treasurer, etc." />
                  </div>
                  <div className="md:col-span-2">
                    <label className="dt6-label">Contact email</label>
                    <input required type="email" className="dt6-input" data-testid="al-email"
                           value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Article III — the mission */}
              <div className="mb-8">
                <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--dt6-primary)] mb-4">
                  / ARTICLE III · THE MISSION
                </div>
                <label className="dt6-label">Mission statement (in your own words)</label>
                <textarea required className="dt6-textarea" rows={5} data-testid="al-mission"
                          value={form.mission_statement} onChange={(e) => set("mission_statement", e.target.value)}
                          placeholder="How does your platform put American citizens first?" />
              </div>

              {/* Article IV — the attestation */}
              <div className="mb-8 pt-6 border-t" style={{ borderColor: "var(--dt6-border)" }}>
                <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--dt6-primary)] mb-4">
                  / ARTICLE IV · FUNDING ATTESTATION
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="al-funding-label">
                  <input type="checkbox" data-testid="al-funding" className="mt-1.5"
                         checked={form.funding_attestation}
                         onChange={(e) => set("funding_attestation", e.target.checked)} />
                  <span className="text-sm text-[color:var(--dt6-text)] leading-relaxed">
                    <b>I attest.</b> This organization accepts no foreign-government funding or direction.
                  </span>
                </label>
              </div>

              {/* Signature block — bottom-border-only, contract-line aesthetic */}
              <div className="pt-6 border-t" style={{ borderColor: "var(--dt6-border)" }}>
                <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--dt6-primary)] mb-4">
                  / SIGNATURE · IN WITNESS WHEREOF
                </div>
                <div className="mb-3">
                  <input
                    required
                    className="dt6-signature-line"
                    data-testid="al-sig"
                    value={form.standard_signature}
                    onChange={(e) => set("standard_signature", e.target.value)}
                    placeholder="Type your full legal name to sign"
                    autoComplete="off"
                  />
                  <div className="mt-2 font-mono text-[0.65rem] tracking-[0.16em] uppercase text-[color:var(--dt6-muted)] flex flex-wrap gap-x-6 gap-y-1">
                    <span>SIGNED · {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                    <span>DIGITALLY TIMESTAMPED · LOGGED ON SUBMISSION</span>
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer mt-5" data-testid="al-agree-label">
                  <input type="checkbox" data-testid="al-agree" className="mt-1"
                         checked={form.standard_agreed}
                         onChange={(e) => set("standard_agreed", e.target.checked)} />
                  <span className="text-xs text-[color:var(--dt6-muted)] leading-relaxed">
                    I sign the DT6 Standard on behalf of the organization above. My electronic
                    signature and timestamp are logged.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="dt6-btn dt6-btn-primary w-full justify-center mt-8 py-4 text-base"
                data-testid="al-submit"
              >
                {busy ? "SUBMITTING…" : "Sign & Submit for Vetting"}
              </button>

              <p className="text-[0.7rem] text-[color:var(--dt6-muted)] italic mt-5 text-center max-w-xl mx-auto" data-testid="alignment-counsel-footer">
                {counselFooter || "…"}
              </p>
            </form>
          </div>
        </Section>
      </div>
      <PublicFooter />
    </>
  );
}
